/**
 * ===== TEMPLATE VALIDATOR - VALIDATION DES TEMPLATES =====
 * Validateur complet pour les templates et leurs propriétés
 */
import { z } from 'zod';
import { logger } from './ProfessionalLogger';

// Schémas de validation
export const TemplateSchema = z.object({
  id: z.string().min(1, 'ID du template requis'),
  name: z.string().min(1, 'Nom du template requis').max(100, 'Nom trop long'),
  description: z.string().max(500, 'Description trop longue').optional(),
  category: z.string().min(1, 'Catégorie requise'),
  tags: z.array(z.string()).max(20, 'Trop de tags'),
  thumbnail: z.string().url('URL de miniature invalide').optional(),
  data: z.object({
    width: z.number().min(100, 'Largeur minimale: 100px').max(4000, 'Largeur maximale: 4000px'),
    height: z.number().min(100, 'Hauteur minimale: 100px').max(4000, 'Hauteur maximale: 4000px'),
    backgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Couleur de fond invalide')
      .optional(),
    objects: z.array(z.unknown()).max(1000, "Trop d'objets dans le template"),
  }),
  metadata: z.object({
    createdAt: z.string().datetime('Date de création invalide'),
    updatedAt: z.string().datetime('Date de mise à jour invalide'),
    version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Version invalide'),
    author: z.string().optional(),
    license: z.string().optional(),
  }),
  isPublic: z.boolean(),
  isPremium: z.boolean(),
});

export const TemplateConfigSchema = z.object({
  width: z.number().min(100).max(4000),
  height: z.number().min(100).max(4000),
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  name: z.string().min(1).max(100).optional(),
});

export const TemplateObjectSchema = z.object({
  type: z.string().min(1, "Type d'objet requis"),
  attrs: z.record(z.unknown()),
  children: z.array(z.unknown()).optional(),
});

// Types TypeScript
export type TemplateData = z.infer<typeof TemplateSchema>;
export type TemplateConfig = z.infer<typeof TemplateConfigSchema>;
export type TemplateObject = z.infer<typeof TemplateObjectSchema>;

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  data?: unknown;
}

export interface TemplateValidationOptions {
  strictMode: boolean;
  allowCustomObjects: boolean;
  maxObjects: number;
  maxFileSize: number;
}

export class TemplateValidator {
  private options: TemplateValidationOptions;

  constructor(options: Partial<TemplateValidationOptions> = {}) {
    this.options = {
      strictMode: false,
      allowCustomObjects: true,
      maxObjects: 1000,
      maxFileSize: 10 * 1024 * 1024, // 10MB
      ...options,
    };
  }

  /**
   * Valide un template complet
   */
  public validateTemplate(template: unknown): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // Validation avec Zod
      const result = TemplateSchema.safeParse(template);
      if (!result.success) {
        result.error.errors.forEach(error => {
          errors.push(`${error.path.join('.')}: ${error.message}`);
        });
      }

      // Validations supplémentaires
      this.validateTemplateStructure(template, errors, warnings);
      this.validateTemplateObjects(template, errors, warnings);
      this.validateTemplatePerformance(template, warnings);

      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        data: result.success ? result.data : null,
      };
    } catch (error) {
      logger.error('Erreur lors de la validation du template:', error);
      errors.push('Erreur interne de validation');
      return { isValid: false, errors, warnings };
    }
  }

  /**
   * Valide la configuration d'un template
   */
  public validateTemplateConfig(config: unknown): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      const result = TemplateConfigSchema.safeParse(config);
      if (!result.success) {
        result.error.errors.forEach(error => {
          errors.push(`${error.path.join('.')}: ${error.message}`);
        });
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        data: result.success ? result.data : null,
      };
    } catch (error) {
      logger.error('Erreur lors de la validation de la config:', error);
      errors.push('Erreur interne de validation');
      return { isValid: false, errors, warnings };
    }
  }

  /**
   * Valide un fichier de template
   */
  public validateTemplateFile(file: File): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const allowedTypes = ['application/json', 'text/plain'];
    const allowedExtensions = ['.json', '.template'];

    // Vérification de la taille
    if (file.size > this.options.maxFileSize) {
      const maxSizeMB = this.options.maxFileSize / (1024 * 1024);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      errors.push(`Fichier trop volumineux: ${fileSizeMB}MB (max: ${maxSizeMB}MB)`);
    }

    // Vérification du type MIME
    if (!allowedTypes.includes(file.type) && file.type !== '') {
      errors.push(`Type de fichier non supporté: ${file.type}`);
    }

    // Vérification de l'extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !allowedExtensions.includes(`.${extension}`)) {
      errors.push(`Extension de fichier non supportée: .${extension}`);
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Valide la structure du template
   */
  private validateTemplateStructure(
    template: unknown,
    errors: string[],
    _warnings: string[]
  ): void {
    if (!template) {
      errors.push('Template vide ou invalide');
      return;
    }

    // Vérifier les propriétés requises
    if (!template || typeof template !== 'object') {
      errors.push('Template invalide: doit être un objet');
      return;
    }

    const templateData = template as {
      id?: unknown;
      name?: unknown;
      data?: { objects?: unknown[] };
    };
    const requiredProps = ['id', 'name', 'data'];
    for (const prop of requiredProps) {
      if (!(prop in templateData)) {
        errors.push(`Propriété requise manquante: ${prop}`);
      }
    }

    // Vérifier les objets
    if (templateData.data?.objects && templateData.data.objects.length > this.options.maxObjects) {
      errors.push(
        `Trop d'objets: ${templateData.data.objects.length} (max: ${this.options.maxObjects})`
      );
    }
  }

  /**
   * Valide les objets du template
   */
  private validateTemplateObjects(template: unknown, errors: string[], warnings: string[]): void {
    if (!template || typeof template !== 'object') return;

    const templateData = template as { data?: { objects?: unknown[] } };
    if (!templateData.data?.objects || !Array.isArray(templateData.data.objects)) {
      return;
    }

    templateData.data.objects.forEach((obj: unknown, index: number) => {
      const typedObj = obj as { type?: string; attrs?: { src?: string; text?: string } };
      if (!typedObj.type) {
        errors.push(`Objet ${index}: type manquant`);
      }

      // Validation des images
      if (typedObj.type === 'Image' && typedObj.attrs?.src) {
        if (!this.isValidImageUrl(typedObj.attrs.src)) {
          warnings.push(`Objet ${index}: URL d'image potentiellement invalide`);
        }
      }

      // Validation du texte
      if (typedObj.type === 'Text' && typedObj.attrs?.text === '') {
        warnings.push(`Objet ${index}: texte vide`);
      }
    });
  }

  /**
   * Valide les performances du template
   */
  private validateTemplatePerformance(template: unknown, warnings: string[]): void {
    if (!template || typeof template !== 'object') return;

    const templateData = template as {
      data?: { width?: number; height?: number; objects?: unknown[] };
    };
    if (!templateData.data) return;

    // Avertir pour les grandes dimensions
    if (templateData.data.width && templateData.data.height) {
      if (templateData.data.width > 2000 || templateData.data.height > 2000) {
        warnings.push('Grande taille de canvas - peut affecter les performances');
      }
    }

    // Avertir pour beaucoup d'objets
    const objectCount = templateData.data.objects?.length || 0;
    if (objectCount > 100) {
      warnings.push(`Nombreux objets (${objectCount}) - peut ralentir l'édition`);
    }

    // Avertir pour les objets complexes
    const complexObjects = templateData.data.objects?.filter((obj: unknown) => {
      const typedObj = obj as { type?: string; attrs?: { shadowBlur?: unknown } };
      return typedObj.type === 'Image' || typedObj.type === 'Text' || typedObj.attrs?.shadowBlur;
    });
    if (complexObjects && complexObjects.length > 50) {
      warnings.push('Nombreux objets complexes - optimisation recommandée');
    }
  }

  /**
   * Vérifie si une URL d'image est valide
   */
  private isValidImageUrl(url: string): boolean {
    try {
      return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
    } catch {
      return false;
    }
  }

  /**
   * Vérifie si une couleur est valide
   */
  private isValidColor(color: string): boolean {
    return /^#[0-9A-Fa-f]{6}$/.test(color) || /^#[0-9A-Fa-f]{3}$/.test(color);
  }

  /**
   * Formate les bytes en format lisible
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Configure les options
   */
  public configure(options: Partial<TemplateValidationOptions>): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Obtient les options actuelles
   */
  public getOptions(): TemplateValidationOptions {
    return { ...this.options };
  }

  /**
   * Valide un objet de template individuel
   */
  public validateTemplateObject(obj: unknown): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      const result = TemplateObjectSchema.safeParse(obj);
      if (!result.success) {
        result.error.errors.forEach(error => {
          errors.push(`${error.path.join('.')}: ${error.message}`);
        });
      }

      // Validation supplémentaire
      if (obj && typeof obj === 'object') {
        const typedObj = obj as { type?: unknown };
        if (!typedObj.type) {
          errors.push("L'objet doit avoir un type");
        }
      } else {
        errors.push("L'objet doit être un objet valide");
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        data: errors.length === 0 ? obj : null,
      };
    } catch (error) {
      logger.error("Erreur lors de la validation de l'objet:", error);
      return {
        isValid: false,
        errors: ["Erreur lors de la validation de l'objet"],
        warnings: [],
      };
    }
  }

  /**
   * Nettoyage des ressources
   */
  public destroy(): void {
    // Rien à nettoyer pour le moment
    logger.info('TemplateValidator détruit');
  }
}

// Instance par défaut
export const templateValidator = new TemplateValidator();
